import React, { useMemo } from 'react';

import { getMeasurementDisplayName } from '../../../constants/measurementLabels';
import type {
  QuickInferenceFrame,
  QuickInferenceSegmentation,
} from '../../../helpers/api/quick-inference/quick-inference';

export interface MeasurementRow {
  id: string;
  name: string;
  value: number;
  unit: 'px' | 'cm';
  kind: 'measurement';
}

function hasCalibratedSpacing(pixelSpacingMm: number | null | undefined): boolean {
  return pixelSpacingMm != null && pixelSpacingMm > 0;
}

function pxToCm(px: number, spacingMm: number): number {
  return (px * spacingMm) / 10;
}

function formatValue(value: number, unit: 'px' | 'cm'): string {
  if (unit === 'px') return `${value.toFixed(1)} px`;
  return `${value.toFixed(2)} cm`;
}

function isMeasurementSeg(seg: QuickInferenceSegmentation): boolean {
  const label = seg.label ?? '';
  return (
    seg.segment_type === 'MEASUREMENT' ||
    (typeof label === 'string' && label.startsWith('MEAS__'))
  );
}

function collectMeasurementRows(
  frame: QuickInferenceFrame,
  segmentations: QuickInferenceSegmentation[],
  pixelSpacingMm: number | null | undefined
): MeasurementRow[] {
  const calibrated = hasCalibratedSpacing(pixelSpacingMm);
  const spacing = pixelSpacingMm ?? 1;
  const byKey = new Map<string, MeasurementRow>();

  if (calibrated && frame.quality_metrics) {
    for (const [_view, viewMetrics] of Object.entries(frame.quality_metrics)) {
      if (!viewMetrics || typeof viewMetrics !== 'object') continue;
      for (const [key, raw] of Object.entries(viewMetrics)) {
        if (!key.startsWith('MEAS__') || typeof raw !== 'number') continue;
        byKey.set(key, {
          id: `qm-${key}`,
          name: getMeasurementDisplayName(key),
          value: raw,
          unit: 'cm',
          kind: 'measurement',
        });
      }
    }
  }

  for (const seg of segmentations) {
    if (!isMeasurementSeg(seg)) continue;

    const label = seg.label ?? '';
    const measKey = label.startsWith('MEAS__')
      ? label.split(' ')[0]
      : label
        ? `MEAS__${label}`
        : '';
    if (!measKey) continue;
    if (byKey.has(measKey)) continue;

    const pxVal = seg.length ?? seg.perimeter ?? seg.area;
    if (pxVal == null || !Number.isFinite(pxVal)) continue;

    byKey.set(measKey, {
      id: `seg-meas-${seg.id ?? measKey}`,
      name: getMeasurementDisplayName(measKey),
      value: calibrated ? pxToCm(pxVal, spacing) : pxVal,
      unit: calibrated ? 'cm' : 'px',
      kind: 'measurement',
    });
  }

  return Array.from(byKey.values());
}

/** Best confidence score per category from frame segmentations (0–1). */
export function collectCategoryScores(
  segmentations: QuickInferenceSegmentation[] | null | undefined
): Map<number, number> {
  const map = new Map<number, number>();
  for (const seg of segmentations || []) {
    const id = Number(seg.category_id);
    if (!Number.isFinite(id) || typeof seg.score !== 'number') continue;
    const prev = map.get(id);
    if (prev == null || seg.score > prev) map.set(id, seg.score);
  }
  return map;
}

interface Props {
  frame: QuickInferenceFrame;
  pixelSpacingMm: number | null | undefined;
}

export function QuickInferenceMeasurementsPanel({ frame, pixelSpacingMm }: Props) {
  const segmentations = frame.segmentations || [];
  const needsCalibration = !hasCalibratedSpacing(pixelSpacingMm);

  const measurements = useMemo(
    () => collectMeasurementRows(frame, segmentations, pixelSpacingMm),
    [frame, segmentations, pixelSpacingMm]
  );

  if (!measurements.length) {
    return null;
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 p-2.5 dark:border-blue-700 dark:from-blue-900/20 dark:to-blue-800/20">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800 dark:text-blue-300">
        Measurements
      </div>
      <div className="space-y-1.5">
        {measurements.map((row) => (
          <div key={row.id} className="flex items-start justify-between gap-2">
            <span className="min-w-0 text-xs leading-snug text-gray-700 dark:text-gray-300">
              {row.name}
            </span>
            <span
              className={`shrink-0 text-xs font-bold tabular-nums ${
                needsCalibration && row.unit === 'px'
                  ? 'text-yellow-600 dark:text-yellow-400'
                  : 'text-blue-700 dark:text-blue-400'
              }`}
            >
              {row.value > 0 ? formatValue(row.value, row.unit) : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
